import { create } from 'zustand';
import api from '../service/api';
import type {
  CreateCardPayload,
  UpdateCardPayload,
} from '../service/api/cards';
import type { Card, CardDelivery } from '../service/api/types';

interface ManagerCardsState {
  cards: Card[];
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchCards: () => Promise<void>;
  createCard: (payload: CreateCardPayload) => Promise<Card>;
  updateCard: (id: string, payload: UpdateCardPayload) => Promise<Card>;
  deleteCard: (id: string) => Promise<void>;
  deliverCard: (cardId: string, userIds: string[]) => Promise<CardDelivery[]>;
  clearError: () => void;
}

export const useManagerCards = create<ManagerCardsState>(set => ({
  cards: [],
  isLoading: false,
  error: null,

  fetchCards: async () => {
    set({ isLoading: true, error: null });

    try {
      const cards = await api.cardsGetAll();
      set({ cards, isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch cards',
        isLoading: false,
      });
      throw error;
    }
  },

  createCard: async (payload: CreateCardPayload) => {
    set({ isLoading: true, error: null });

    try {
      const card = await api.cardCreate(payload);
      set(state => ({
        cards: [card, ...state.cards],
        isLoading: false,
      }));
      return card;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to create card',
        isLoading: false,
      });
      throw error;
    }
  },

  updateCard: async (id: string, payload: UpdateCardPayload) => {
    set({ isLoading: true, error: null });

    try {
      const updatedCard = await api.cardUpdate(id, payload);
      set(state => ({
        cards: state.cards.map(card => (card._id === id ? updatedCard : card)),
        isLoading: false,
      }));
      return updatedCard;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update card',
        isLoading: false,
      });
      throw error;
    }
  },

  deleteCard: async (id: string) => {
    set({ isLoading: true, error: null });

    try {
      await api.cardDelete(id);
      set(state => ({
        cards: state.cards.filter(card => card._id !== id),
        isLoading: false,
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete card',
        isLoading: false,
      });
      throw error;
    }
  },

  deliverCard: async (cardId: string, userIds: string[]) => {
    set({ isLoading: true, error: null });

    try {
      const deliveries = await api.cardDeliver(cardId, userIds);
      set({ isLoading: false });
      return deliveries;
    } catch (error) {
      set({
        error:
          error instanceof Error ? error.message : 'Failed to deliver card',
        isLoading: false,
      });
      throw error;
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));
